import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  useToast,
  useColorModeValue,
  Icon,
  Badge,
  Textarea,
  Select,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  IconButton,
  Tooltip,
  Divider
} from '@chakra-ui/react';
import { 
  CalendarIcon, 
  AddIcon, 
  EditIcon, 
  DeleteIcon,
  TimeIcon,
  ViewIcon
} from '@chakra-ui/icons';
import { 
  FiCalendar, 
  FiClock, 
  FiUsers, 
  FiVideo,
  FiRefreshCw,
  FiFilter,
  FiTrendingUp,
  FiPlay,
  FiPause,
  FiSettings
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import Sidebar2 from '../shared/Sidebar2';
import CalendarAdmin from '../shared/CalendarAdmin';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from './../services/axiosInstance';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const EventsContent = () => {
  const { isHovered } = useSidebar();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [events, setEvents] = useState([]);
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [image, setImage] = useState(null);
  const [professor, setProfessor] = useState('');
  const [eventType, setEventType] = useState('lecture');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [editingEvent, setEditingEvent] = useState(null);

  const toast = useToast();

  // Color scheme based on home page palette
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('خطأ في جلب الأحداث:', error);
      toast({
        title: 'خطأ في تحميل الأحداث',
        description: 'فشل في تحميل قائمة الأحداث',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!eventName.trim()) {
      newErrors.eventName = 'اسم الحدث مطلوب';
    }

    if (!startDate) {
      newErrors.startDate = 'تاريخ البدء مطلوب';
    }

    if (!endDate) {
      newErrors.endDate = 'تاريخ الانتهاء مطلوب';
    }

    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      newErrors.endDate = 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البدء';
    }

    if (!professor.trim()) {
      newErrors.professor = 'اسم المحاضر مطلوب';
    }

    if (maxParticipants && (isNaN(maxParticipants) || parseInt(maxParticipants) <= 0)) {
      newErrors.maxParticipants = 'عدد المشاركين يجب أن يكون رقماً موجباً';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDateClick = (date) => {
    const selectedDate = new Date(date.start);

    if (!isNaN(selectedDate.getTime())) {
      const endDateWithAddedHour = new Date(selectedDate);
      endDateWithAddedHour.setHours(endDateWithAddedHour.getHours() + 1);

      setStartDate(selectedDate);
      setEndDate(endDateWithAddedHour);
      setEditingEvent(null);
      resetForm();
      onOpen();
    } else {
      console.error('تاريخ غير صالح تم النقر عليه');
    }
  };

  const resetForm = () => {
    setEventName('');
    setEventDescription('');
    setIsLive(false);
    setImage(null);
    setProfessor('');
    setEventType('lecture');
    setMaxParticipants('');
    setErrors({});
  };

  const handleCreateEvent = async () => {
    if (!validateForm()) {
      return;
    }

    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      toast({
        title: 'خطأ في التاريخ',
        description: 'قيم التاريخ غير صالحة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const finalEventName = isLive ? `🔴 بث مباشر: ${eventName}` : eventName;

    try {
      setLoading(true);
      const newEvent = {
        title: finalEventName,
        description: eventDescription,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        professor: professor,
        eventType: eventType,
        maxParticipants: maxParticipants ? parseInt(maxParticipants) : null,
        isLive: isLive
      };

      const formData = new FormData();
      Object.keys(newEvent).forEach(key => {
        if (newEvent[key] !== null && newEvent[key] !== undefined) {
          formData.append(key, newEvent[key]);
        }
      });

      if (image) {
        formData.append('imageUrl', image);
      }

      let response;
      if (editingEvent) {
        response = await axiosInstance.put(`/events/${editingEvent._id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast({
          title: 'تم التحديث بنجاح',
          description: 'تم تحديث الحدث بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        response = await axiosInstance.post('/events', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast({
          title: 'تم الإنشاء بنجاح',
          description: 'تم إنشاء الحدث بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      await fetchEvents();
      resetForm();
      setStartDate(null);
      setEndDate(null);
      setEditingEvent(null);
      onClose();

    } catch (error) {
      console.error('خطأ في إنشاء/تحديث الحدث:', error);
      toast({
        title: 'خطأ في العملية',
        description: editingEvent ? 'فشل في تحديث الحدث' : 'فشل في إنشاء الحدث',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventName(event.title.replace('🔴 بث مباشر: ', ''));
    setEventDescription(event.description || '');
    setIsLive(event.title.includes('🔴 بث مباشر:'));
    setStartDate(new Date(event.start));
    setEndDate(new Date(event.end));
    setProfessor(event.professor || '');
    setEventType(event.eventType || 'lecture');
    setMaxParticipants(event.maxParticipants ? event.maxParticipants.toString() : '');
    setErrors({});
    onOpen();
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axiosInstance.delete(`/events/${eventId}`);
      await fetchEvents();
      toast({
        title: 'تم الحذف بنجاح',
        description: 'تم حذف الحدث بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('خطأ في حذف الحدث:', error);
      toast({
        title: 'خطأ في الحذف',
        description: 'فشل في حذف الحدث',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getEventStats = () => {
    const totalEvents = events.length;
    const liveEvents = events.filter(event => event.title.includes('🔴 بث مباشر:')).length;
    const upcomingEvents = events.filter(event => new Date(event.start) > new Date()).length;
    const todayEvents = events.filter(event => {
      const eventDate = new Date(event.start);
      const today = new Date();
      return eventDate.toDateString() === today.toDateString();
    }).length;

    return { totalEvents, liveEvents, upcomingEvents, todayEvents };
  };

  const stats = getEventStats();

  const eventTypeOptions = [
    { value: 'lecture', label: 'محاضرة' },
    { value: 'workshop', label: 'ورشة عمل' },
    { value: 'exam', label: 'امتحان' },
    { value: 'meeting', label: 'اجتماع' },
    { value: 'seminar', label: 'ندوة' },
    { value: 'other', label: 'أخرى' }
  ];

  if (loading && events.length === 0) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Cairo, sans-serif">جاري تحميل الأحداث...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif" mr={250}>
      <Flex direction={{ base: 'column', md: 'row' }}>
        <Sidebar2 />
        <Box
          flex="1"
          ml={{ base: '0', md: '250px' }}
          p={6}
          bg={bgColor}
          transition="margin-left 0.3s ease"
          minHeight="100vh"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading 
                      as="h1" 
                      size="xl" 
                      color={secondaryColor} 
                      fontFamily="Cairo, sans-serif"
                      fontWeight="700"
                    >
                      إدارة الأحداث والفعاليات
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      تنظيم وإدارة الأحداث التعليمية والمحاضرات
                    </Text>
                  </VStack>
                  
                  <HStack spacing={3} flexDirection={{ base: 'column', sm: 'row' }} w={{ base: 'full', md: 'auto' }}>
                    <Button
                      bg={primaryColor}
                      color={secondaryColor}
                      _hover={{ bg: '#d4b016' }}
                      borderRadius="lg"
                      fontWeight="600"
                      leftIcon={<AddIcon />}
                      onClick={() => {
                        resetForm();
                        setEditingEvent(null);
                        const now = new Date();
                        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
                        setStartDate(now);
                        setEndDate(oneHourLater);
                        onOpen();
                      }}
                      fontFamily="Cairo, sans-serif"
                    >
                      إنشاء حدث جديد
                    </Button>
                    
                    <IconButton
                      icon={<FiRefreshCw />}
                      aria-label="تحديث"
                      bg="white"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: primaryColor, bg: `${primaryColor}10` }}
                      borderRadius="lg"
                      onClick={fetchEvents}
                      isLoading={loading}
                    />
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Statistics */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            إجمالي الأحداث
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.totalEvents}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            جميع الأحداث المجدولة
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg={`${primaryColor}20`} 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiCalendar} color={primaryColor} boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            أحداث اليوم
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.todayEvents}
                          </StatNumber>
                          <StatHelpText color="green.500" fontSize="sm">
                            <StatArrow type="increase" />
                            مجدولة اليوم
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="green.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiClock} color="green.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            الأحداث القادمة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.upcomingEvents}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            في المستقبل
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="blue.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiTrendingUp} color="blue.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            البث المباشر
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.liveEvents}
                          </StatNumber>
                          <StatHelpText color="red.500" fontSize="sm">
                            أحداث مباشرة
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="red.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiVideo} color="red.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Calendar */}
          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
              <CardHeader>
                <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                  التقويم التفاعلي
                </Heading>
              </CardHeader>
              <CardBody>
                <CalendarAdmin
                  fetchEvents={fetchEvents}
                  events={events}
                  onDateClick={handleDateClick}
                  onEventClick={handleEditEvent}
                  onEventDelete={handleDeleteEvent}
                />
              </CardBody>
            </Card>
          </motion.div>

          {/* Event Creation/Edit Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" fontFamily="Cairo, sans-serif">
              <ModalHeader bg={secondaryColor} color="white" borderTopRadius="xl">
                <HStack spacing={3}>
                  <Icon as={editingEvent ? EditIcon : AddIcon} />
                  <Text>
                    {editingEvent ? 'تعديل الحدث' : 'إنشاء حدث جديد'}
                  </Text>
                </HStack>
              </ModalHeader>
              <ModalCloseButton color="white" />
              <ModalBody p={6}>
                <VStack spacing={6} align="stretch">
                  <FormControl isInvalid={errors.eventName}>
                    <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                      اسم الحدث
                    </FormLabel>
                    <Input
                      value={eventName}
                      onChange={(e) => {
                        setEventName(e.target.value);
                        if (errors.eventName) {
                          setErrors(prev => ({ ...prev, eventName: null }));
                        }
                      }}
                      placeholder="أدخل اسم الحدث"
                      bg="gray.50"
                      border="2px solid"
                      borderColor={errors.eventName ? "red.300" : "gray.200"}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                    />
                    <FormErrorMessage>{errors.eventName}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                      وصف الحدث
                    </FormLabel>
                    <Textarea
                      value={eventDescription}
                      onChange={(e) => setEventDescription(e.target.value)}
                      placeholder="أدخل وصف مفصل للحدث (اختياري)"
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                      rows={3}
                    />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={errors.startDate}>
                      <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                        تاريخ ووقت البدء
                      </FormLabel>
                      <Input
                        type="datetime-local"
                        value={startDate ? startDate.toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          setStartDate(new Date(e.target.value));
                          if (errors.startDate) {
                            setErrors(prev => ({ ...prev, startDate: null }));
                          }
                        }}
                        bg="gray.50"
                        border="2px solid"
                        borderColor={errors.startDate ? "red.300" : "gray.200"}
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      />
                      <FormErrorMessage>{errors.startDate}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.endDate}>
                      <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                        تاريخ ووقت الانتهاء
                      </FormLabel>
                      <Input
                        type="datetime-local"
                        value={endDate ? endDate.toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          setEndDate(new Date(e.target.value));
                          if (errors.endDate) {
                            setErrors(prev => ({ ...prev, endDate: null }));
                          }
                        }}
                        bg="gray.50"
                        border="2px solid"
                        borderColor={errors.endDate ? "red.300" : "gray.200"}
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      />
                      <FormErrorMessage>{errors.endDate}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={errors.professor}>
                      <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                        اسم المحاضر
                      </FormLabel>
                      <Input
                        value={professor}
                        onChange={(e) => {
                          setProfessor(e.target.value);
                          if (errors.professor) {
                            setErrors(prev => ({ ...prev, professor: null }));
                          }
                        }}
                        placeholder="أدخل اسم المحاضر"
                        bg="gray.50"
                        border="2px solid"
                        borderColor={errors.professor ? "red.300" : "gray.200"}
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      />
                      <FormErrorMessage>{errors.professor}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                        نوع الحدث
                      </FormLabel>
                      <Select
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      >
                        {eventTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isInvalid={errors.maxParticipants}>
                    <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                      الحد الأقصى للمشاركين (اختياري)
                    </FormLabel>
                    <Input
                      type="number"
                      value={maxParticipants}
                      onChange={(e) => {
                        setMaxParticipants(e.target.value);
                        if (errors.maxParticipants) {
                          setErrors(prev => ({ ...prev, maxParticipants: null }));
                        }
                      }}
                      placeholder="أدخل الحد الأقصى للمشاركين"
                      bg="gray.50"
                      border="2px solid"
                      borderColor={errors.maxParticipants ? "red.300" : "gray.200"}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                      min="1"
                    />
                    <FormErrorMessage>{errors.maxParticipants}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                      صورة الحدث (اختياري)
                    </FormLabel>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImage(e.target.files[0])}
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                      p={2}
                    />
                  </FormControl>

                  <Divider />

                  <FormControl>
                    <Checkbox
                      isChecked={isLive}
                      onChange={(e) => setIsLive(e.target.checked)}
                      colorScheme="red"
                      size="lg"
                    >
                      <HStack spacing={2}>
                        <Icon as={FiVideo} color="red.500" />
                        <Text fontWeight="600" color={textColor} fontFamily="Cairo, sans-serif">
                          بث مباشر
                        </Text>
                      </HStack>
                    </Checkbox>
                    <Text fontSize="sm" color="gray.500" mt={2} fontFamily="Cairo, sans-serif">
                      سيتم إضافة علامة البث المباشر إلى اسم الحدث
                    </Text>
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter bg="gray.50" borderBottomRadius="xl">
                <HStack spacing={3}>
                  <Button 
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: '#d4b016' }}
                    onClick={handleCreateEvent}
                    isLoading={loading}
                    loadingText={editingEvent ? "جاري التحديث..." : "جاري الإنشاء..."}
                    fontFamily="Cairo, sans-serif"
                  >
                    {editingEvent ? 'حفظ التغييرات' : 'إنشاء الحدث'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      onClose();
                      resetForm();
                      setEditingEvent(null);
                    }}
                    fontFamily="Cairo, sans-serif"
                  >
                    إلغاء
                  </Button>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </Flex>
    </Box>
  );
};

const AdminEvents = () => {
  return (
    <SidebarProvider>
      <EventsContent />
    </SidebarProvider>
  );
};

export default AdminEvents;

